import "./AppPayBankAccounts.css";
import { useState, useEffect } from "react";
import { Address, CheckBankAccount, CheckResponseError, CreateCheckBankAccountPayload, isCheckError, UpdateCheckBankAccountPayload } from "winslow-core";
import { Button } from "../../../ds2/components";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Input } from "../../../ds2/components";
import { apiCheckCreateBankAccount, apiCheckUpdateBankAccount } from "../../../services/PayrollService";

// Helper function to check if bank account is CheckBankAccountRawType
const isRawType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { raw_bank_account: any }> => {
    return "raw_bank_account" in account;
};

type AppPayBankAccountProps = {
    bankAccount?: CheckBankAccount | null;
    checkCompanyId?: string | null; // For company bank accounts
    checkEmployeeId?: string | null; // For employee bank accounts
    checkContractorId?: string | null; // For contractor bank accounts
    onClose?: () => void;
    onUpdate?: (bankAccount: CheckBankAccount) => void;
    onCreate?: (bankAccount: CheckBankAccount) => void;
    onError?: (error: CheckResponseError | undefined) => void;
};

export const AppPayBankAccount = ({ 
    bankAccount, 
    checkCompanyId, 
    checkEmployeeId,
    checkContractorId, 
    onClose, 
    onUpdate, 
    onCreate,
    onError 
}: AppPayBankAccountProps) => {
    const isEditing = !!bankAccount;
    const isRawAccount = bankAccount ? isRawType(bankAccount) : true;

    const [saving, setSaving] = useState<boolean>(false);
    const [isEditingAccountNumber, setIsEditingAccountNumber] = useState<boolean>(false);
    const [originalAccountNumberLastFour, setOriginalAccountNumberLastFour] = useState<string>("");
    const [originalFormData, setOriginalFormData] = useState<{
        institution_name: string;
        routing_number: string;
        subtype: "checking" | "savings";
    } | null>(null);

    // Form data for creating/editing bank accounts
    // raw type only
    const [formData, setFormData] = useState<{
        institution_name: string;
        account_number: string;
        routing_number: string;
        subtype: "checking" | "savings";
    }>({
        institution_name: "",
        account_number: "",
        routing_number: "",
        subtype: "checking",
    });

    // Subtype options
    const subtypeOptions: GenericSelectOption[] = [
        { displayName: "Checking", value: "checking" },
        { displayName: "Savings", value: "savings" },
    ];

    // Initialize form data from bankAccount prop when editing
    useEffect(() => {
        if (bankAccount && isRawType(bankAccount)) {
            const lastFour = bankAccount.raw_bank_account.account_number_last_four || "";
            const routingNumber = (bankAccount.raw_bank_account.routing_number || "").replace(/\D/g, "");
            
            setFormData({
                institution_name: bankAccount.raw_bank_account.institution_name || "",
                account_number: lastFour,
                routing_number: routingNumber,
                subtype: bankAccount.raw_bank_account.subtype || "checking",
            });
            
            setOriginalAccountNumberLastFour(lastFour);
            setOriginalFormData({
                institution_name: bankAccount.raw_bank_account.institution_name || "",
                routing_number: routingNumber,
                subtype: bankAccount.raw_bank_account.subtype || "checking",
            });
            setIsEditingAccountNumber(false);
        } else if (!bankAccount) {
            // Reset form when not editing
            setFormData({
                institution_name: "",
                account_number: "",
                routing_number: "",
                subtype: "checking",
            });
            setOriginalAccountNumberLastFour("");
            setOriginalFormData(null);
            setIsEditingAccountNumber(false);
        }
    }, [bankAccount]);

    const handleSave = async () => {
        if (!isRawAccount) {
            toast("You can not edit Plaid bank accounts", toastFail);
            return;
        }

        if (!formData.institution_name.trim()) {
            toast("Institution name is required", toastFail);
            return;
        }

        // Only require account number for new accounts, or if editing and user is actively editing the account number
        if (!isEditing && !formData.account_number.trim()) {
            toast("Account number is required", toastFail);
            return;
        }

        // Validate account number length
        const accountNumberDigits = formData.account_number.replace(/\D/g, "");
        
        // For new accounts, require at least 8 digits
        if (!isEditing && accountNumberDigits.length < 8) {
            toast("Account number must be at least 8 digits", toastFail);
            return;
        }

        // For updates, only validate if user is actively editing the account number
        if (isEditing && isEditingAccountNumber) {
            if (accountNumberDigits.length <= 4) {
                toast("Please enter the full account number", toastFail);
                return;
            }
            if (accountNumberDigits.length < 8) {
                toast("Account number must be at least 8 digits", toastFail);
                return;
            }
        }

        if (!formData.routing_number.trim()) {
            toast("Routing number is required", toastFail);
            return;
        }

        // Validate routing number is exactly 9 digits
        const routingNumberDigits = formData.routing_number.replace(/\D/g, "");
        if (routingNumberDigits.length !== 9) {
            toast("Routing number must be exactly 9 digits", toastFail);
            return;
        }

        // Use cleaned routing number (digits only) for saving
        const cleanedRoutingNumber = routingNumberDigits;
        
        // Use cleaned account number (digits only) for saving
        const cleanedAccountNumber = accountNumberDigits;

        // Determine the ID to use for API calls
        const entityId = checkEmployeeId || checkCompanyId || checkContractorId;
        if (!entityId) {
            toast("Company, Employee, or Contractor ID is required", toastFail);
            return;
        }

        setSaving(true);
        try {
            if (isEditing && bankAccount && originalFormData) {
                if (!isRawType(bankAccount)) {
                    toast("You can not edit Plaid bank accounts", toastFail);
                    setSaving(false);
                    return;
                }

                const hasNewAccountNumber = isEditingAccountNumber && cleanedAccountNumber.length > 4 && cleanedAccountNumber !== originalAccountNumberLastFour;
               
                const payload: UpdateCheckBankAccountPayload = {
                    bank_account: bankAccount.id,
                    raw_bank_account: {
                        institution_name: formData.institution_name,
                        account_number: hasNewAccountNumber ? cleanedAccountNumber : originalAccountNumberLastFour,
                        routing_number: cleanedRoutingNumber,
                        subtype: formData.subtype,
                    },
                };

                // Make API call
                const apiResponse = await apiCheckUpdateBankAccount(bankAccount.id, payload);
                const updatedBankAccount = apiResponse?.data;
                const checkResponseError = apiResponse?.checkResponseError;

                if (apiResponse && isCheckError(apiResponse) && onError) {
                    onError(checkResponseError);
                }
            
                if (!apiResponse || isCheckError(apiResponse) || !updatedBankAccount) {
                    toast("Error updating bank account", toastFail);
                    setSaving(false);
                    return;
                }

                toast("Bank account updated successfully", toastSuccess);
                
                // Only call callback on success
                if (onUpdate) {
                    onUpdate(updatedBankAccount);
                }
            } else {
                // For creation, we'll create a raw bank account
                const payload: CreateCheckBankAccountPayload = {
                    employee: checkEmployeeId || "",
                    company: checkCompanyId || "",
                    contractor: checkContractorId || "",
                    raw_bank_account: {
                        institution_name: formData.institution_name,
                        account_number: cleanedAccountNumber,
                        routing_number: cleanedRoutingNumber,
                        subtype: formData.subtype,
                    },
                };

                // Make API call
                const apiResponse = await apiCheckCreateBankAccount(entityId, payload);
                const createdBankAccount = apiResponse?.data;
                const checkResponseError = apiResponse?.checkResponseError;

                if (apiResponse && isCheckError(apiResponse) && onError) {
                    onError(checkResponseError);
                }

                if (!apiResponse || isCheckError(apiResponse) || !createdBankAccount) {
                    toast("Error creating bank account", toastFail);
                    setSaving(false);
                    return;
                }

                toast("Bank account created successfully", toastSuccess);
                
                // Only call callback on success
                if (onCreate) {
                    onCreate(createdBankAccount);
                }
            }
        } catch (error) {
            console.error("[AppPayBankAccount] Error saving bank account:", error);
            toast("Error saving bank account", toastFail);
            if (onError) {
                onError(undefined);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="app-pay-bank-accounts-form">
            <div className="app-pay-bank-accounts-form-header">
                <h3>{isEditing ? "Edit Bank Account" : "Add Bank Account"}</h3>
            </div>
            <div className="app-pay-bank-accounts-form-content">
                <div className="app-custom-property-label">
                    Name *
                </div>
                <div className="app-custom-property-value">
                    <Input
                        type="text"
                        placeholder="Enter institution name"
                        value={formData.institution_name}
                        onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                    />
                </div>

                <div className="app-custom-property-label">
                    Account Number {isEditing ? "" : "*"}
                </div>
                <div className="app-custom-property-value">
                    {isEditing && !isEditingAccountNumber ? (
                        <div
                            style={{
                                padding: "10px 12px",
                                border: "1px solid var(--container-border-color)",
                                borderRadius: "var(--border-radius)",
                                backgroundColor: "var(--background-color-primary)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                minHeight: "40px",
                                width: "100%",
                            }}
                            onClick={() => {
                                setIsEditingAccountNumber(true);
                                // Clear the account number field when starting to edit
                                setFormData({ ...formData, account_number: "" });
                            }}
                        >
                            <span style={{ color: "var(--text-color-primary)" }}>
                                ****{originalAccountNumberLastFour}
                            </span>
                        </div>
                    ) : (
                        <div style={{ position: "relative", width: "100%" }}>
                            <Input
                                type="text"
                                placeholder={isEditing ? "Enter full account number (min 8 digits)" : "Enter account number (min 8 digits)"}
                                value={formData.account_number}
                                onChange={(e) => {
                                    // Only allow digits
                                    const digitsOnly = e.target.value.replace(/\D/g, "");
                                    setFormData({ ...formData, account_number: digitsOnly });
                                }}
                                style={isEditing && isEditingAccountNumber ? { paddingRight: "40px" } : {}}
                            />
                            {isEditing && isEditingAccountNumber && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditingAccountNumber(false);
                                        setFormData({ ...formData, account_number: originalAccountNumberLastFour });
                                    }}
                                    style={{
                                        position: "absolute",
                                        right: "8px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "var(--text-color-secondary)",
                                    }}
                                    title="Cancel editing account number"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="app-custom-property-label">
                    Routing Number *
                </div>
                <div className="app-custom-property-value">
                    <Input
                        type="text"
                        placeholder="Enter 9-digit routing number"
                        value={formData.routing_number}
                        onChange={(e) => {
                            // Only allow digits, limit to 9 characters
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                            setFormData({ ...formData, routing_number: digitsOnly });
                        }}
                        maxLength={9}
                    />
                </div>

                <div className="app-custom-property-label">
                    Account Type *
                </div>
                <div className="app-custom-property-value">
                    <GenericSelect
                        options={subtypeOptions}
                        selected={subtypeOptions.find(opt => opt.value === formData.subtype)}
                        onChange={(value: any) => {
                            setFormData({ ...formData, subtype: value as "checking" | "savings" });
                        }}
                        maxHeight="200px"
                    />
                </div>
            </div>
            <div className="app-pay-bank-accounts-form-actions">
                {onClose && 
                    <Button
                        label="Cancel"
                        variant="secondary"
                        onClick={onClose}
                    />
                }
                <Button
                    label={isEditing ? "Update" : "Create"}
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || Boolean(
                        !formData.institution_name.trim() || 
                        !formData.routing_number.trim() || 
                        formData.routing_number.replace(/\D/g, "").length !== 9 ||
                        (!isEditing && (!formData.account_number.trim() || formData.account_number.replace(/\D/g, "").length < 8)) ||
                        (isEditing && isEditingAccountNumber && (formData.account_number.replace(/\D/g, "").length <= 4 || formData.account_number.replace(/\D/g, "").length < 8))
                    )}
                />
            </div>
        </div>
    );
};

